import { useState, useCallback } from 'react'
import { useUpdateFinding, usePostComment } from '../../hooks/useReviews'
import type { Finding, TimelineEvent } from '../../gen/types'

export interface FindingCardProps {
  finding: Finding
  reviewId: string
  comments: TimelineEvent[]
  isResolved: boolean
  onResolve?: () => void
  onUnresolve?: () => void
  anchorText?: string | null
  onAnchorClick?: () => void
  hideAccent?: boolean
}

interface SeverityStyle {
  pill: string
  stripe: string
  bg: string
  headerBg: string
  headerText: string
  icon: string
}

function severityToStyle(sev: string): SeverityStyle {
  const lower = sev === 'block' ? 'blocking' : sev
  switch (lower) {
    case 'blocking':
      return {
        pill: 'sev sev-blocking',
        stripe: 'border-l-sev-blocking',
        bg: 'bg-sev-blocking/[0.04]',
        headerBg: 'bg-sev-blocking/[0.14]',
        headerText: 'text-sev-blocking',
        icon: '⛔',
      }
    case 'must-fix':
    case 'warning':
      return {
        pill: 'sev sev-must-fix',
        stripe: 'border-l-sev-must-fix',
        bg: 'bg-sev-must-fix/[0.04]',
        headerBg: 'bg-sev-must-fix/[0.14]',
        headerText: 'text-sev-must-fix',
        icon: '▲',
      }
    case 'suggest':
      return {
        pill: 'sev sev-suggest',
        stripe: 'border-l-sev-suggest',
        bg: 'bg-sev-suggest/[0.04]',
        headerBg: 'bg-sev-suggest/[0.14]',
        headerText: 'text-sev-suggest',
        icon: '✦',
      }
    default:
      return {
        pill: 'sev sev-nit',
        stripe: 'border-l-border-strong',
        bg: '',
        headerBg: 'bg-overlay',
        headerText: 'text-fg-muted',
        icon: '·',
      }
  }
}

export function FindingCard({
  finding,
  reviewId,
  comments,
  isResolved,
  onResolve,
  onUnresolve,
  anchorText,
  onAnchorClick,
  hideAccent,
}: FindingCardProps) {
  const [replyOpen, setReplyOpen] = useState(false)
  const [replyText, setReplyText] = useState('')
  const [localComment, setLocalComment] = useState<string | null>(null)
  const updateFinding = useUpdateFinding(reviewId)
  const postComment = usePostComment(reviewId)

  const handleResolve = useCallback(() => {
    updateFinding.mutate(
      { findingId: finding.id, status: 'resolved' },
      { onSuccess: () => onResolve?.() },
    )
  }, [finding.id, onResolve, updateFinding])

  const handleUnresolve = useCallback(() => {
    updateFinding.mutate(
      { findingId: finding.id, status: 'open' },
      { onSuccess: () => onUnresolve?.() },
    )
  }, [finding.id, onUnresolve, updateFinding])

  const handleSubmitReply = useCallback(() => {
    const text = replyText.trim()
    if (!text) return
    postComment.mutate(
      { message: text, findingId: finding.id },
      {
        onSuccess: () => {
          setLocalComment(text)
          setReplyText('')
          setReplyOpen(false)
        },
      },
    )
  }, [replyText, postComment, finding.id])

  const style = severityToStyle(finding.severity)
  const sevLabel = finding.severity === 'block' ? 'blocking' : finding.severity

  return (
    <div
      className={[
        hideAccent ? 'flex' : 'flex flex-col',
        'border border-border-default rounded-md overflow-hidden',
        hideAccent ? style.bg : '',
        isResolved ? 'opacity-60' : '',
        hideAccent ? `border-l-[3px] ${style.stripe}` : '',
      ].join(' ')}
    >
      {!hideAccent && (
        <div
          className={[
            'flex items-center gap-2 px-3 py-1.5 border-b border-border-subtle',
            'font-display text-[10px] font-bold uppercase tracking-[0.08em]',
            style.headerBg,
            style.headerText,
          ].join(' ')}
        >
          <span aria-hidden className="text-[11px] leading-none">{style.icon}</span>
          <span>{sevLabel}</span>
          {finding.category && (
            <>
              <span aria-hidden className="text-fg-dim opacity-60">·</span>
              <span className="font-mono font-normal normal-case tracking-normal text-fg-dim">
                {finding.category}
              </span>
            </>
          )}
          {anchorText && onAnchorClick && (
            <>
              <span aria-hidden className="text-fg-dim opacity-60">·</span>
              <button
                className="font-mono font-normal normal-case tracking-normal text-link hover:underline bg-transparent border-0 p-0 cursor-pointer"
                onClick={onAnchorClick}
              >
                {anchorText} ↗
              </button>
            </>
          )}
        </div>
      )}

      <div
        className={[
          'flex-1 px-3 py-[7px] flex flex-col gap-[5px] min-w-0',
          hideAccent ? '' : style.bg,
        ].join(' ')}
      >
        {hideAccent && (
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className={style.pill}>{sevLabel}</span>
            {anchorText && onAnchorClick && (
              <button
                className="text-link text-[10px] font-mono hover:underline bg-transparent border-0 p-0 cursor-pointer"
                onClick={onAnchorClick}
              >
                {anchorText} ↗
              </button>
            )}
            {finding.category && (
              <span className="text-fg-dim text-[10px] font-mono border border-border-subtle rounded-[3px] px-1.5 py-[1px]">
                {finding.category}
              </span>
            )}
          </div>
        )}

        <div
          className={[
            'text-[12px] font-medium leading-[1.5]',
            isResolved ? 'text-fg-dim line-through' : 'text-fg',
          ].join(' ')}
        >
          {finding.claim}
        </div>

        {finding.rationale && (
          <div className="text-[11px] text-fg-muted leading-[1.5]">{finding.rationale}</div>
        )}

        {comments.map((c) => (
          <div
            key={c.id}
            className="text-[11px] text-fg-muted bg-canvas border border-border-subtle rounded-[3px] px-2 py-1.5 mt-1"
          >
            <span className="text-fg-dim text-[10px] block mb-0.5">
              {c.actorKind} · {c.actorId.slice(0, 14)}
            </span>
            {c.message}
          </div>
        ))}

        {localComment && !comments.some((c) => c.message === localComment) && (
          <div className="text-[11px] text-fg-muted bg-canvas border border-border-subtle rounded-[3px] px-2 py-1.5 mt-1">
            <span className="text-fg-dim text-[10px] block mb-0.5">you · just now</span>
            {localComment}
          </div>
        )}

        <div className="flex gap-1.5 mt-1.5">
          <button
            className="btn btn-default"
            onClick={() => setReplyOpen((o) => !o)}
          >
            ↩ Reply
          </button>
          {isResolved ? (
            <button
              className="btn btn-changes"
              onClick={handleUnresolve}
              disabled={updateFinding.isPending}
            >
              ↺ Unresolve
            </button>
          ) : (
            <button
              className="btn btn-approve"
              onClick={handleResolve}
              disabled={updateFinding.isPending}
            >
              ✓ Resolve
            </button>
          )}
        </div>

        {replyOpen && (
          <div className="mt-2 flex flex-col gap-1.5">
            <textarea
              className="textarea text-[12px]"
              placeholder="Add a comment or request changes…"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              rows={3}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSubmitReply()
                }
              }}
            />
            <div className="flex gap-1.5 justify-end">
              <button className="btn btn-default" onClick={() => setReplyOpen(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSubmitReply}
                disabled={postComment.isPending || !replyText.trim()}
              >
                {postComment.isPending ? 'Sending…' : 'Submit'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
